import React from 'react';
import { Link } from 'react-router-dom';
import { motion, useScroll, useSpring } from 'framer-motion';
import {
  ShieldCheckIcon,
  UserGroupIcon,
  BadgeCheckIcon,
  ClockIcon,
  CheckCircleIcon,
  MailIcon,
  LockClosedIcon,
  ClipboardCheckIcon,
  SearchIcon,
  StarIcon,
  SparklesIcon,
  ChevronRightIcon
} from '@heroicons/react/outline';

// Everything on this page must stay literally true of the product as it
// exists today. No invented metrics, testimonials, or capabilities — if a
// claim can't be verified against what we've shipped, it doesn't go here.

const whyPoints = [
  {
    icon: SearchIcon,
    title: 'Trusted protection is hard to find',
    description:
      'Hiring private security today runs on word of mouth and guesswork. There is no easy way to find a qualified professional or check their credentials.'
  },
  {
    icon: BadgeCheckIcon,
    title: 'Credentials should be verifiable',
    description:
      'Florida licenses security professionals through FDACS, but almost nobody checks. Our team reviews every GUARDLY Security Service Provider application and verifies every license against the official FDACS registry.'
  },
  {
    icon: UserGroupIcon,
    title: 'Professionals deserve better work',
    description:
      'Licensed guards, veterans, and former law enforcement have hard-earned skills and few flexible ways to use them. GUARDLY lets Security Service Providers control when they work and how often.'
  }
];

const statusItems = [
  {
    label: 'Security Service Provider applications',
    state: 'Open now',
    active: true,
    description:
      'We are onboarding licensed security professionals across Florida and reviewing every application individually.'
  },
  {
    label: 'Client waitlist',
    state: 'Open now',
    active: true,
    description:
      'Individuals and businesses can join the waitlist today and will be first in line when booking opens.'
  },
  {
    label: 'Bookings',
    state: 'Coming at launch',
    active: false,
    description:
      'Booking opens once the vetted Security Service Provider network is in place. Waitlist members will be notified first.'
  }
];

const tiers = [
  {
    name: 'Tier 1',
    requirement: 'Florida Class D license',
    description: 'Licensed unarmed security professionals.'
  },
  {
    name: 'Tier 2',
    requirement: 'Florida Class G license',
    description: 'Licensed armed security professionals.'
  },
  {
    name: 'Tier 3',
    requirement: 'Military, law enforcement, executive protection',
    description: 'Active or retired military, law enforcement, executive protection, or specialized security.'
  }
];

const vettingSteps = [
  {
    icon: ClipboardCheckIcon,
    title: 'Application review',
    description: 'Our team reviews every application individually, usually within 24-48 hours.'
  },
  {
    icon: BadgeCheckIcon,
    title: 'License verification',
    description: 'Florida Class D and G licenses are verified against the official FDACS license registry.'
  },
  {
    icon: ShieldCheckIcon,
    title: 'Background check consent',
    description: 'Every applicant consents to a background check as part of the application.'
  },
  {
    icon: LockClosedIcon,
    title: 'Document security',
    description: 'Uploaded documents travel over encrypted connections, are scanned for malware, and are stored encrypted. Only the review team can open them, through time-limited links.'
  }
];

const LandingPage = () => {
  const { scrollYProgress } = useScroll();
  const scaleX = useSpring(scrollYProgress, {
    stiffness: 100,
    damping: 30,
    restDelta: 0.001
  });

  return (
    <div className="bg-black text-white overflow-hidden">
      {/* Progress Bar */}
      <motion.div
        className="fixed top-0 left-0 right-0 h-1 bg-primary-600 z-50 origin-left"
        style={{ scaleX }}
      />

      {/* Hero */}
      <section className="relative min-h-screen flex items-center">
        <div className="absolute inset-0">
          <div className="absolute inset-0 bg-gradient-to-br from-gray-900 via-black to-gray-900" />
          <motion.div
            className="absolute inset-0"
            style={{
              background: 'radial-gradient(circle at 50% 50%, rgba(59, 130, 246, 0.1) 0%, transparent 50%)'
            }}
            animate={{ scale: [1, 1.1, 1] }}
            transition={{ duration: 8, repeat: Infinity, ease: 'easeInOut' }}
          />
        </div>

        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-24">
          <div className="text-center max-w-4xl mx-auto">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8 }}
            >
              <motion.div
                initial={{ opacity: 0, scale: 0.9 }}
                animate={{ opacity: 1, scale: 1 }}
                className="inline-flex items-center px-6 py-3 bg-primary-500/10 border border-primary-500/30 rounded-full mb-8"
              >
                <SparklesIcon className="h-5 w-5 text-primary-400 mr-2" aria-hidden="true" />
                <span className="text-primary-300 font-medium">Now launching in Florida</span>
              </motion.div>

              <h1 className="text-5xl lg:text-7xl font-bold mb-6">
                <span className="block">Private Protection,</span>
                <span className="block text-transparent bg-clip-text bg-gradient-to-r from-primary-400 to-primary-600">
                  Properly Vetted
                </span>
              </h1>

              <p className="text-xl text-gray-300 mb-8 max-w-2xl mx-auto">
                GUARDLY connects people and businesses with licensed private security
                professionals. We review every application and verify every
                license with the State of Florida.
              </p>

              <div className="flex flex-col sm:flex-row gap-4 justify-center mb-12">
                <Link
                  to="/apply"
                  className="group relative inline-flex items-center justify-center px-8 py-4 font-semibold text-white transition-all duration-200 bg-primary-600 rounded-xl hover:bg-primary-700 transform hover:scale-105"
                >
                  <span className="flex items-center">
                    Become a Security Service Provider
                    <ChevronRightIcon className="ml-2 h-5 w-5 group-hover:translate-x-1 transition-transform" aria-hidden="true" />
                  </span>
                </Link>
                <Link
                  to="/waitlist"
                  className="inline-flex items-center justify-center px-8 py-4 font-semibold text-white transition-all duration-200 bg-white/10 backdrop-blur-sm border border-white/20 rounded-xl hover:bg-white/20"
                >
                  Join the Client Waitlist
                </Link>
              </div>

              <p className="text-sm text-gray-500">
                Security Service Provider applications are open now. Bookings open at launch, and
                waitlist members are notified first.
              </p>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Why we're building GUARDLY */}
      <section className="py-20 bg-gray-900">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="text-4xl lg:text-5xl font-bold mb-4">Why We're Building GUARDLY</h2>
            <p className="text-xl text-gray-400 max-w-3xl mx-auto">
              Private protection shouldn't depend on who you happen to know.
            </p>
          </motion.div>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            {whyPoints.map((point, index) => (
              <motion.div
                key={point.title}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
              >
                <div className="bg-gradient-to-br from-gray-800 to-gray-900 rounded-2xl p-8 h-full border border-gray-700 hover:border-primary-500/50 transition-all">
                  <div className="bg-primary-500/10 w-16 h-16 rounded-2xl flex items-center justify-center mb-6">
                    <point.icon className="h-8 w-8 text-primary-400" aria-hidden="true" />
                  </div>
                  <h3 className="text-2xl font-bold mb-4">{point.title}</h3>
                  <p className="text-gray-300">{point.description}</p>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Where we are today */}
      <section className="py-20 bg-black">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="text-4xl lg:text-5xl font-bold mb-4">Where We Are Today</h2>
            <p className="text-xl text-gray-400 max-w-3xl mx-auto">
              Here is exactly where things stand.
            </p>
          </motion.div>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            {statusItems.map((item, index) => (
              <motion.div
                key={item.label}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className={`rounded-2xl p-8 border ${
                  item.active
                    ? 'bg-gradient-to-br from-primary-900/30 to-gray-900 border-primary-500/40'
                    : 'bg-gray-900 border-gray-700'
                }`}
              >
                <div
                  className={`inline-flex items-center text-xs font-semibold px-3 py-1 rounded-full mb-4 ${
                    item.active ? 'bg-green-500/20 text-green-400' : 'bg-gray-700 text-gray-300'
                  }`}
                >
                  {item.state}
                </div>
                <h3 className="text-2xl font-bold mb-3">{item.label}</h3>
                <p className="text-gray-300">{item.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Become a Security Service Provider */}
      <section className="py-20 bg-gray-900">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-start">
            <motion.div
              initial={{ opacity: 0, x: -20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
            >
              <h2 className="text-4xl lg:text-5xl font-bold mb-6">Become a Security Service Provider</h2>
              <p className="text-xl text-gray-300 mb-8">
                If you hold a Florida Class D or Class G license, we want to hear from
                you. The same goes for military, law enforcement, and executive
                protection backgrounds.
              </p>

              <ul className="space-y-4 mb-8">
                <li className="flex items-start">
                  <ClockIcon className="h-6 w-6 text-primary-400 mr-3 flex-shrink-0" aria-hidden="true" />
                  <div>
                    <p className="font-semibold">The application takes about 15 minutes</p>
                    <p className="text-sm text-gray-400">
                      Your progress saves in your browser as you go, so you can come back
                      and finish later.
                    </p>
                  </div>
                </li>
                <li className="flex items-start">
                  <CheckCircleIcon className="h-6 w-6 text-primary-400 mr-3 flex-shrink-0" aria-hidden="true" />
                  <div>
                    <p className="font-semibold">You review everything before submitting</p>
                    <p className="text-sm text-gray-400">
                      A full summary of your answers, with the chance to edit any section.
                    </p>
                  </div>
                </li>
                <li className="flex items-start">
                  <MailIcon className="h-6 w-6 text-primary-400 mr-3 flex-shrink-0" aria-hidden="true" />
                  <div>
                    <p className="font-semibold">A real person follows up</p>
                    <p className="text-sm text-gray-400">
                      Applications are reviewed within 24-48 hours and our team follows up
                      by email. If we just need one more document, we will ask for it.
                    </p>
                  </div>
                </li>
              </ul>

              <Link
                to="/apply"
                className="group inline-flex items-center justify-center px-8 py-4 font-semibold text-white transition-all duration-200 bg-primary-600 rounded-xl hover:bg-primary-700"
              >
                Start Your Application
                <ChevronRightIcon className="ml-2 h-5 w-5 group-hover:translate-x-1 transition-transform" aria-hidden="true" />
              </Link>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              className="space-y-4"
            >
              <h3 className="text-2xl font-bold mb-2">GUARDLY Tiers</h3>
              {tiers.map((tier) => (
                <div
                  key={tier.name}
                  className="bg-gradient-to-br from-gray-800 to-gray-900 rounded-2xl p-6 border border-gray-700"
                >
                  <div className="flex items-center mb-2">
                    <StarIcon className="h-5 w-5 text-primary-400 mr-2" aria-hidden="true" />
                    <span className="text-lg font-bold">{tier.name}</span>
                    <span className="ml-3 text-sm text-primary-300">{tier.requirement}</span>
                  </div>
                  <p className="text-gray-400 text-sm">{tier.description}</p>
                </div>
              ))}
              <p className="text-xs text-gray-500">
                Florida licenses are verified through the FDACS license registry as part
                of every review.
              </p>
            </motion.div>
          </div>
        </div>
      </section>

      {/* How we vet */}
      <section className="py-20 bg-black">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="text-4xl lg:text-5xl font-bold mb-4">How We Vet</h2>
            <p className="text-xl text-gray-400 max-w-3xl mx-auto">
              This is what happens to every application.
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {vettingSteps.map((step, index) => (
              <motion.div
                key={step.title}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className="bg-gray-900 border border-gray-700 rounded-2xl p-6 hover:border-primary-500/50 transition-all"
              >
                <step.icon className="h-10 w-10 text-primary-400 mb-4" aria-hidden="true" />
                <h3 className="text-lg font-bold mb-2">{step.title}</h3>
                <p className="text-sm text-gray-400">{step.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Waitlist CTA */}
      <section className="py-20 bg-gradient-to-b from-gray-900 to-black relative overflow-hidden">
        <div className="absolute inset-0">
          <div className="absolute inset-0 bg-gradient-to-t from-primary-900/20 to-transparent" />
        </div>

        <div className="relative z-10 max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <h2 className="text-4xl lg:text-6xl font-bold mb-6">Need Protection?</h2>
            <p className="text-xl mb-8 text-gray-300 max-w-2xl mx-auto">
              Booking opens once the Security Service Provider network is in place. Join the waitlist
              and we will email you when it does.
            </p>

            <div className="flex flex-col sm:flex-row gap-4 justify-center mb-12">
              <Link
                to="/waitlist"
                className="group relative inline-flex items-center justify-center px-8 py-4 font-semibold text-black transition-all duration-200 bg-white rounded-xl hover:bg-gray-100 transform hover:scale-105"
              >
                <SparklesIcon className="mr-2 h-5 w-5" aria-hidden="true" />
                <span>Join the Waitlist</span>
              </Link>
              <Link
                to="/apply"
                className="inline-flex items-center justify-center px-8 py-4 font-semibold text-white transition-all duration-200 bg-white/10 backdrop-blur-sm border border-white/20 rounded-xl hover:bg-white/20"
              >
                Become a Security Service Provider
              </Link>
            </div>

            <div className="border-t border-gray-800 pt-8">
              <p className="text-gray-400 mb-4">For investors and partners:</p>
              <a
                href="mailto:contact@guardly.app"
                className="inline-flex items-center text-primary-400 hover:text-primary-300 font-semibold"
              >
                <MailIcon className="mr-2 h-5 w-5" aria-hidden="true" />
                contact@guardly.app
              </a>
            </div>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default LandingPage;
